<script setup lang="ts">
  import { reactive, ref, computed, onMounted } from 'vue';
  import { UiModalHeader, ColoredButton, ThemeProvider } from '@/shared/ui';
  import {
    useEditHomologation,
    resolveTrainingParticipantUserIdStrings,
    resolveTrainingBranchIdForHomologation,
    resolveTrainingMemberIdForUser,
    buildBranchUserLookup,
    buildBranchMemberOptions,
    getUserPositionsFromLookup,
    getMemberPositionsFromUsers,
    useHomologationDateState,
    useTrainingSessions,
    useHomologationFormState,
  } from '../model';
  import {
    UserPositionDateSection,
    DocumentNameSection,
    TrainingSection,
    ObservationsSection,
    EffectiveDateSection,
  } from './components';
  import {
    usePositionUserSelection,
    fetchAllMembersAsUsers,
    resolveMembershipBranchIdForMapping,
    usePermittedMembers,
  } from '@/entities/User';
  import { useBranchTheme } from '@/entities/Branch';
  import { useCustomToast } from '@/shared/lib';
  import type { DocumentRevision } from '@/entities/Document';
  import type { User } from '@/entities/User';
  import type { Position } from '@/entities/Position';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import type { EditHomologationFormData } from '../model/editHomologation.types';

  const props = defineProps<{
    title: string;
    revision: DocumentRevision | null;
    users?: User[];
    positions?: Position[];
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'saved'): void;
  }>();

  const { populateFormFromRevision, saveHomologation, isLoading, error } = useEditHomologation();
  const toast = useCustomToast();
  const { getCurrentSelectedBranch } = useBranchTheme();
  const branchMembers = ref<User[]>([]);

  const resolvedBranchId = computed<number | undefined>(() => {
    const revisionBranchId =
      props.revision?.document?.branchId ?? props.revision?.document?.branch?.id;
    if (revisionBranchId !== undefined && revisionBranchId !== null) {
      const n = Number(revisionBranchId);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }

    const selectedBranchId = getCurrentSelectedBranch();
    if (selectedBranchId !== undefined && selectedBranchId !== null && selectedBranchId !== '') {
      const n = Number(selectedBranchId);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
    }

    return undefined;
  });

  const {
    positions,
    users,
    selectedPositions,
    selectedUsers,
    fetchPositionsAndUsers,
    initializeSelections,
  } = usePositionUserSelection({
    mode: 'by-selected-positions',
    branchId: resolvedBranchId,
    getUserId: (user) => user.userId ?? user.id,
    includeInactiveMembers: true,
  });
  const { users: elaborationUsers, fetchUsers: fetchElaborationUsers } = usePermittedMembers({
    branchId: resolvedBranchId,
    requiredPermissions: [{ action: 'send', subject: 'Document' }],
  });
  const { users: approvalUsers, fetchUsers: fetchApprovalUsers } = usePermittedMembers({
    branchId: resolvedBranchId,
    requiredPermissions: [{ action: 'manageApproval', subject: 'Homologation' }],
  });
  const { users: verificationUsers, fetchUsers: fetchVerificationUsers } = usePermittedMembers({
    branchId: resolvedBranchId,
    requiredPermissions: [{ action: 'manageVerification', subject: 'Homologation' }],
  });

  const formData = reactive<EditHomologationFormData>({
    hasTraining: false,
    observations: '',
  });

  const {
    elaborationDate,
    approvalDate,
    verificationDate,
    effectiveDate,
    elaborationDatePickerOpen,
    approvalDatePickerOpen,
    verificationDatePickerOpen,
    effectiveDatePickerOpen,
    formatDate,
  } = useHomologationDateState(formData);

  const {
    sessions,
    addSession,
    removeSession,
    canAddMore,
    getSessionUserId,
    setSessionUserId,
    getSessionPositionId,
    setSessionPositionId,
    getSessionDate,
    setSessionDate,
    getSessionDatePickerOpen,
    setSessionDatePickerOpen,
  } = useTrainingSessions(formData);

  const {
    elaborationUserId,
    elaborationPositionId,
    approvalUserId,
    approvalPositionId,
    verificationUserId,
    verificationPositionId,
    hasTrainingString,
    effectiveDateError,
    canSave,
    setElaborationUserId,
    setElaborationPositionId,
    setApprovalUserId,
    setApprovalPositionId,
    setVerificationUserId,
    setVerificationPositionId,
    setHasTraining,
    saveInitialState,
    initialFormData,
  } = useHomologationFormState(formData, { selectedPositions, selectedUsers, isLoading });

  onMounted(async () => {
    const branchForMembers = resolveMembershipBranchIdForMapping(resolvedBranchId.value);

    const loadBranchMembers = async (): Promise<User[]> => {
      try {
        return branchForMembers !== undefined
          ? await fetchAllMembersAsUsers(branchForMembers, undefined, { includeInactiveMembers: true })
          : [];
      } catch {
        toast.showErrorToast({
          title: 'Erro',
          message: 'Não foi possível carregar a lista de membros da filial.',
          type: 'error',
        });
        return [];
      }
    };

    const [, loadedBranchMembers] = await Promise.all([
      fetchPositionsAndUsers(),
      loadBranchMembers(),
      fetchElaborationUsers(),
      fetchApprovalUsers(),
      fetchVerificationUsers(),
    ]);
    branchMembers.value = loadedBranchMembers;

    if (props.revision) {
      const populated = await populateFormFromRevision(
        props.revision,
        branchMembers.value,
        positions.value.data || []
      );

      Object.assign(formData, populated);

      const posIds = populated.trainingPositionIds?.map((id) => id.toString()) ?? [];
      const trainingBranchId = resolveTrainingBranchIdForHomologation(
        props.revision?.document?.branchId,
        getCurrentSelectedBranch()
      );

      await initializeSelections(posIds, (eligibleUsers) =>
        resolveTrainingParticipantUserIdStrings(
          populated.trainingMemberIds,
          eligibleUsers.concat(branchMembers.value),
          trainingBranchId
        )
      );

      saveInitialState();
    }
  });

  const mergedBranchScopedUsers = computed(() => [
    ...(users.value.data ?? []),
    ...branchMembers.value,
    ...(elaborationUsers.value.data ?? []),
    ...(approvalUsers.value.data ?? []),
    ...(verificationUsers.value.data ?? []),
  ]);

  const userByIdMap = computed(() =>
    buildBranchUserLookup(mergedBranchScopedUsers.value, resolvedBranchId.value)
  );

  const findUserById = (userId: number) => userByIdMap.value.get(userId);

  const withRecordedMember = (
    memberId: number | undefined,
    memberName: string | undefined,
    options: Array<{ id: number; name: string }>
  ): Array<{ id: number; name: string }> => {
    if (
      memberId != null &&
      !Number.isNaN(memberId) &&
      memberName &&
      !options.some((option) => option.id === memberId)
    ) {
      return [...options, { id: memberId, name: memberName }];
    }
    return options;
  };

  const elaborationBranchUserOptions = computed(() =>
    withRecordedMember(
      initialFormData.value?.elaborationEditedByUserId,
      initialFormData.value?.elaborationUserName,
      buildBranchMemberOptions(elaborationUsers.value.data ?? [], resolvedBranchId.value)
    )
  );

  const approvalBranchUserOptions = computed(() =>
    withRecordedMember(
      initialFormData.value?.approvalUserId,
      initialFormData.value?.approvalUserName,
      buildBranchMemberOptions(approvalUsers.value.data ?? [], resolvedBranchId.value)
    )
  );

  const verificationBranchUserOptions = computed(() =>
    withRecordedMember(
      initialFormData.value?.verificationUserId,
      initialFormData.value?.verificationUserName,
      buildBranchMemberOptions(verificationUsers.value.data ?? [], resolvedBranchId.value)
    )
  );

  const trainingResponsibleUserOptions = computed(() =>
    buildBranchMemberOptions(branchMembers.value, resolvedBranchId.value)
  );

  const getUserPositions = (userId: number | string): Array<{ id: number; name: string }> => {
    return getUserPositionsFromLookup(userId, userByIdMap.value, resolvedBranchId.value);
  };

  const getMemberPositions = (memberId: number | string): Array<{ id: number; name: string }> => {
    return getMemberPositionsFromUsers(
      memberId,
      mergedBranchScopedUsers.value,
      resolvedBranchId.value
    );
  };

  const withRecordedPosition = (
    memberId: number | string,
    positionId: number | undefined,
    positionName: string | undefined
  ): Array<{ id: number; name: string }> => {
    const options = getMemberPositions(memberId);
    if (
      positionId != null &&
      !Number.isNaN(positionId) &&
      positionName &&
      !options.map((option) => option.id).includes(positionId)
    ) {
      return [...options, { id: positionId, name: positionName }];
    }
    return options;
  };

  const resolveTrainingPositionIdsForSelectedUsers = (): number[] => {
    const selectedPositionIds = new Set(
      selectedPositions.value.map((id) => Number.parseInt(id, 10)).filter((id) => !Number.isNaN(id))
    );

    const positionIds = new Set<number>();
    for (const selectedUserId of selectedUsers.value) {
      const userPositions = getUserPositions(selectedUserId);
      for (const position of userPositions) {
        if (selectedPositionIds.has(position.id)) {
          positionIds.add(position.id);
        }
      }
    }

    return [...positionIds];
  };

  const handleSave = async () => {
    if (!props.revision?.id) return;

    try {
      const trainingBranchId = resolveTrainingBranchIdForHomologation(
        props.revision?.document?.branchId,
        getCurrentSelectedBranch()
      );
      if (trainingBranchId === undefined) {
        toast.showErrorToast({
          title: 'Erro',
          message:
            'Não foi possível identificar a filial do documento. Selecione a filial e tente novamente.',
          type: 'error',
        });
        return;
      }

      const trainingPositionIdNums = resolveTrainingPositionIdsForSelectedUsers();
      const trainingMemberIds = selectedUsers.value.map((idStr) => {
        const userId = Number.parseInt(idStr, 10);
        const user = findUserById(userId);
        return resolveTrainingMemberIdForUser(user, trainingBranchId, trainingPositionIdNums);
      });
      if (trainingMemberIds.some((id) => id === undefined)) {
        toast.showErrorToast({
          title: 'Erro',
          message:
            'Não foi possível identificar o vínculo de membro na filial para um dos colaboradores selecionados.',
          type: 'error',
        });
        return;
      }

      const {
        previousRevisionEffectiveDate,
        currentVerificationDate,
        elaborationPositionName,
        approvalPositionName,
        verificationPositionName,
        elaborationUserName,
        approvalUserName,
        verificationUserName,
        ...cleanFormData
      } = formData;
      const formDataToSend: EditHomologationFormData = {
        ...cleanFormData,
        trainingPositionIds: trainingPositionIdNums,
        trainingMemberIds: trainingMemberIds as number[],
      };

      // The observations field maps to the training `notes` when there is
      // training, and to the approval `justification` when there is not.
      if (formData.hasTraining) {
        formDataToSend.trainingNotes = formData.observations;
        formDataToSend.observations = undefined;
      } else {
        formDataToSend.observations = formData.observations;
        formDataToSend.trainingNotes = undefined;
      }

      await saveHomologation(props.revision.id, formDataToSend);
      toast.showSuccessToast({
        title: 'Sucesso',
        message: 'Edição salva com sucesso',
        type: 'success',
      });
      emit('saved');
      emit('close');
    } catch {
      toast.showErrorToast({
        title: 'Erro',
        message: error.value || 'Erro ao salvar configurações. Tente novamente',
        type: 'error',
      });
    }
  };

  const handleCancel = () => {
    emit('close');
  };

  defineExpose({
    handleSave,
    getUserPositions,
    withRecordedPosition,
    withRecordedMember,
    elaborationBranchUserOptions,
    approvalBranchUserOptions,
    verificationBranchUserOptions,
  });
</script>

<template>
  <div class="flex flex-col bg-white-01 rounded-2xl w-[85rem] max-h-[90vh]">
    <ThemeProvider class="flex flex-col min-h-0">
      <UiModalHeader
        class="shrink-0 px-6 py-4 text-sm"
        :title="props.title"
        @close="handleCancel"
      />
      <div
        id="modal-content"
        class="flex flex-col flex-1 min-h-0 overflow-auto"
      >
        <div class="flex flex-col divide-y divide-gray-02">
          <DocumentNameSection
            :prefix="revision?.document?.prefix"
            :document-name="revision?.document?.name"
            :revision="revision?.revision"
          />

          <UserPositionDateSection
            label="Elaboração"
            date-label="Data da elaboração"
            :state="{
              memberId: elaborationUserId,
              positionId: elaborationPositionId,
              date: elaborationDate,
              dateString: formData.elaborationCreatedAt,
              datePickerOpen: elaborationDatePickerOpen,
            }"
            :actions="{
              setMemberId: setElaborationUserId,
              setPositionId: setElaborationPositionId,
              setDate: (val: Date | null) => (elaborationDate = val),
              setDatePickerOpen: (val: boolean) => (elaborationDatePickerOpen = val),
            }"
            :helpers="{
              userOptions: elaborationBranchUserOptions,
              getUserPositions: (memberId: number | string) =>
                withRecordedPosition(
                  memberId,
                  formData.elaborationEditedByPositionId,
                  formData.elaborationPositionName
                ),
              formatDate: formatDate,
            }"
          />

          <UserPositionDateSection
            label="Aprovação"
            date-label="Data da aprovação"
            :state="{
              memberId: approvalUserId,
              positionId: approvalPositionId,
              date: approvalDate,
              dateString: formData.approvalCreatedAt,
              datePickerOpen: approvalDatePickerOpen,
            }"
            :actions="{
              setMemberId: setApprovalUserId,
              setPositionId: setApprovalPositionId,
              setDate: (val: Date | null) => (approvalDate = val),
              setDatePickerOpen: (val: boolean) => (approvalDatePickerOpen = val),
            }"
            :helpers="{
              userOptions: approvalBranchUserOptions,
              getUserPositions: (memberId: number | string) =>
                withRecordedPosition(
                  memberId,
                  formData.approvalPositionId,
                  formData.approvalPositionName
                ),
              formatDate: formatDate,
            }"
          />

          <TrainingSection
            v-model:selected-positions="selectedPositions"
            v-model:selected-users="selectedUsers"
            :has-training-string="hasTrainingString"
            :has-training="formData.hasTraining"
            :sessions="sessions"
            :can-add-more="canAddMore"
            :positions="positions"
            :users="users"
            :user-options="trainingResponsibleUserOptions"
            :form-data="formData"
            :get-user-positions="getMemberPositions"
            :format-date="formatDate"
            :set-has-training="setHasTraining"
            :get-session-user-id="getSessionUserId"
            :set-session-user-id="setSessionUserId"
            :get-session-position-id="getSessionPositionId"
            :set-session-position-id="setSessionPositionId"
            :get-session-date="getSessionDate"
            :set-session-date="setSessionDate"
            :get-session-date-picker-open="getSessionDatePickerOpen"
            :set-session-date-picker-open="setSessionDatePickerOpen"
            :add-session="addSession"
            :remove-session="removeSession"
          />

          <ObservationsSection
            v-model="formData.observations"
            :has-training="formData.hasTraining"
          />

          <UserPositionDateSection
            label="Verificação"
            date-label="Data da verificação"
            :state="{
              memberId: verificationUserId,
              positionId: verificationPositionId,
              date: verificationDate,
              dateString: formData.verificationCreatedAt,
              datePickerOpen: verificationDatePickerOpen,
            }"
            :actions="{
              setMemberId: setVerificationUserId,
              setPositionId: setVerificationPositionId,
              setDate: (val: Date | null) => (verificationDate = val),
              setDatePickerOpen: (val: boolean) => (verificationDatePickerOpen = val),
            }"
            :helpers="{
              userOptions: verificationBranchUserOptions,
              getUserPositions: (memberId: number | string) =>
                withRecordedPosition(
                  memberId,
                  formData.verificationPositionId,
                  formData.verificationPositionName
                ),
              formatDate: formatDate,
            }"
          />

          <EffectiveDateSection
            :date="effectiveDate"
            :date-picker-open="effectiveDatePickerOpen"
            :date-string="formData.effectiveDate"
            :format-date="formatDate"
            :set-date="(val: Date | null) => (effectiveDate = val)"
            :set-date-picker-open="(val: boolean) => (effectiveDatePickerOpen = val)"
            :error-message="effectiveDateError"
          />
        </div>
      </div>

      <div class="shrink-0 flex justify-end gap-3 p-6 border-t border-gray-02">
        <ColoredButton
          text="Cancelar"
          outlined
          :disabled="isLoading"
          @click="handleCancel"
        />
        <ColoredButton
          text="Salvar"
          :icon="CheckMarkIcon"
          :disabled="!canSave"
          :loading="isLoading"
          @click="handleSave"
        />
      </div>
    </ThemeProvider>
  </div>
</template>

<style scoped>
  @media print {
    .modal-header {
      display: none;
    }
  }
</style>
